import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "../components/Header";
import ProGate from "../components/ProGate";
import { fetchEvent, getCachedEventRanking, fetchEventWatchers, API } from "../lib/api";
import { ArrowLeft, Stack, Sparkle, Trophy, ArrowsClockwise, CaretUp, CaretDown, UsersThree } from "@phosphor-icons/react";
import { formatPct, formatSignedPct, formatUSD, formatEndDate } from "../lib/format";
import { motion } from "framer-motion";
import { useAuth } from "../auth/AuthProvider";

export default function EventDetail() {
  const { id } = useParams();
  const { isPro } = useAuth();
  const [event, setEvent] = useState(null);
  const [ranking, setRanking] = useState(null);
  const [loadingRank, setLoadingRank] = useState(false);
  const [streamChars, setStreamChars] = useState(0);
  const [streamText, setStreamText] = useState("");
  const [watchers, setWatchers] = useState({ count: 0, streaming: false });
  const [err, setErr] = useState(null);
  const esRef = useRef(null);

  useEffect(() => {
    fetchEvent(id).then(setEvent).catch(() => setEvent(null));
    getCachedEventRanking(id).then((r) => r && setRanking(r));
    // Poll watcher count every 5s
    const poll = () =>
      fetchEventWatchers(id)
        .then((w) => setWatchers({ count: w.watchers, streaming: w.streaming }))
        .catch(() => {});
    poll();
    const iv = setInterval(poll, 5000);
    return () => {
      clearInterval(iv);
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
    };
  }, [id]);

  const run = async (force = false) => {
    setLoadingRank(true);
    setErr(null);
    setStreamChars(0);
    setStreamText("");
    if (esRef.current) esRef.current.close();
    const es = new EventSource(`${API}/events/${id}/rank/stream`);
    esRef.current = es;
    let total = 0;
    let buffer = "";
    es.addEventListener("delta", (e) => {
      try {
        const { text } = JSON.parse(e.data);
        total += text.length;
        buffer += text;
        setStreamChars(total);
        const m = buffer.match(/"summary"\s*:\s*"([^"\\]*(?:\\.[^"\\]*)*)/);
        if (m && m[1]) {
          setStreamText(m[1].replace(/\\n/g, "\n").replace(/\\"/g, '"'));
        } else {
          // Fallback: until 'summary' field appears in streamed JSON, show
          // a lightly-cleaned tail of the raw buffer so user sees activity.
          const tail = buffer
            .replace(/[\{\}\[\]"]/g, " ")
            .replace(/\\n/g, " ")
            .replace(/\s+/g, " ")
            .trim()
            .slice(-280);
          if (tail) setStreamText(tail);
        }
      } catch {}
    });
    es.addEventListener("complete", (e) => {
      try {
        const r = JSON.parse(e.data);
        setRanking(r);
      } catch {}
      es.close();
      esRef.current = null;
      setLoadingRank(false);
    });
    es.addEventListener("error", (e) => {
      try {
        const { error: msg } = JSON.parse(e.data || "{}");
        if (msg) setErr(msg);
      } catch {}
    });
    es.addEventListener("end", () => {
      es.close();
      esRef.current = null;
      setLoadingRank(false);
    });
    es.onerror = () => {
      es.close();
      esRef.current = null;
      setLoadingRank(false);
      if (total === 0) setErr("Stream connection failed");
    };
  };

  return (
    <div className="min-h-screen bg-background" data-testid="event-detail-page">
      <Header />
      <div className="mx-auto max-w-[1200px] px-6 py-10">
        <Link
          to="/events"
          className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-zinc-400 hover:text-white mb-6"
        >
          <ArrowLeft size={14} /> All events
        </Link>

        {!event ? (
          <div className="text-zinc-500 font-mono text-xs uppercase tracking-widest">Loading event…</div>
        ) : (
          <>
            <div className="border border-white/10 p-6 mb-6 bg-zinc-950/40">
              <div className="flex items-start gap-5">
                {event.image && (
                  <img src={event.image} alt="" className="w-16 h-16 object-cover border border-white/10" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.25em] font-mono text-zinc-500 mb-2 flex items-center gap-3">
                    <span>
                      <Stack size={11} className="inline mr-1" />
                      {event.category || "Event"} · {event.marketCount} outcomes · {formatEndDate(event.endDate)}
                    </span>
                    {watchers.count > 0 && (
                      <span
                        data-testid="event-watchers-pill"
                        className={`inline-flex items-center gap-1 px-2 py-0.5 border ${
                          watchers.streaming ? "border-edge text-edge" : "border-white/15 text-zinc-400"
                        }`}
                        title={watchers.streaming ? "Live ranking in progress" : "Currently viewing"}
                      >
                        <UsersThree size={11} weight={watchers.streaming ? "fill" : "regular"} />
                        {watchers.count} watching
                        {watchers.streaming && (
                          <span className="ml-1 h-1.5 w-1.5 rounded-full bg-edge animate-pulse-soft" />
                        )}
                      </span>
                    )}
                  </div>
                  <h1 className="font-display text-2xl sm:text-3xl font-semibold leading-tight">
                    {event.title}
                  </h1>
                  <div className="mt-3 text-xs font-mono text-zinc-400 tabular-nums">
                    Volume {formatUSD(event.volume)} · Liquidity {formatUSD(event.liquidity)}
                  </div>
                </div>
              </div>

              <button
                data-testid="rank-event-btn"
                onClick={() => isPro ? run(false) : window.scrollTo({ top: 800, behavior: "smooth" })}
                disabled={loadingRank}
                className={`mt-6 inline-flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-[0.18em] px-5 py-2.5 transition-colors disabled:opacity-60 ${
                  isPro
                    ? "bg-edge text-black hover:bg-white"
                    : "bg-transparent border border-edge/40 text-edge hover:bg-edge/10"
                }`}
              >
                <Sparkle size={14} weight="fill" />
                {isPro
                  ? (ranking ? "Re-rank with AI" : "Rank outcomes with AI")
                  : "Unlock AI ranking (Pro)"}
              </button>
            </div>

            {!isPro && <ProGate className="mb-6" feature="AI outcome ranking" />}

            {loadingRank && (
              <div className="border border-edge/40 ai-edge-ring p-6 mb-6">
                <div className="flex items-center gap-3 text-edge">
                  <Sparkle size={18} weight="fill" className="animate-pulse-soft" />
                  <span className="font-display text-xs uppercase tracking-[0.22em]">
                    MFSMAI Oracle · Ranking all {event.marketCount} outcomes…
                  </span>
                  <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-edge">
                    {streamChars > 0 ? `${streamChars} chars streamed` : "starting…"}
                  </span>
                </div>
                {streamText && (
                  <div
                    data-testid="event-stream-text"
                    className="mt-5 border-l-2 border-edge pl-4 max-h-56 overflow-y-auto text-sm font-body text-zinc-200 leading-relaxed whitespace-pre-wrap"
                  >
                    {streamText}
                    <span className="inline-block w-1.5 h-4 align-text-bottom bg-edge animate-pulse-soft ml-0.5" />
                  </div>
                )}
              </div>
            )}

            {err && (
              <div className="border border-red-500/40 p-4 text-sm text-red-300 mb-6 font-body">
                {err}
              </div>
            )}

            {ranking && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="event-ranking-panel"
                className="border border-white/10 ai-edge-ring p-6 mb-6 bg-zinc-950/60"
              >
                <div className="flex items-start justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <Trophy size={18} weight="duotone" className="text-edge" />
                    <div>
                      <div className="font-display text-sm uppercase tracking-[0.22em] text-edge">
                        AI Outcome Ranking
                      </div>
                      <div className="text-[10px] font-mono text-zinc-500 mt-0.5 uppercase tracking-widest">
                        MFSMAI Oracle · {new Date(ranking.generatedAt).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => run(true)}
                    data-testid="event-rerank-btn"
                    className="text-xs font-mono uppercase tracking-widest text-zinc-400 hover:text-white border border-white/15 hover:border-white/40 px-3 py-1.5 transition-colors inline-flex items-center gap-1.5"
                  >
                    <ArrowsClockwise size={12} /> Refresh
                  </button>
                </div>

                {ranking.summary && (
                  <div className="border-l-2 border-edge pl-4 mb-6">
                    <p className="text-sm text-zinc-300 font-body leading-relaxed">
                      {ranking.summary}
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  {ranking.outcomes.map((o, i) => {
                    const isTop = o.marketId === ranking.topPick;
                    const edge = o.edge || 0;
                    const edgeColor = edge > 0 ? "text-profit" : edge < 0 ? "text-loss" : "text-zinc-400";
                    return (
                      <Link
                        key={o.marketId}
                        to={`/market/${o.marketId}`}
                        data-testid={`event-outcome-${o.marketId}`}
                        className={`flex items-center gap-4 p-3 border ${
                          isTop ? "border-edge bg-edge/5" : "border-white/10 hover:border-white/30"
                        } transition-colors group`}
                      >
                        <div className="font-mono text-xl tabular-nums text-zinc-400 w-7">
                          {i + 1}
                        </div>
                        {o.image && (
                          <img src={o.image} alt="" className="w-9 h-9 object-cover border border-white/10" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-display text-sm font-medium truncate flex items-center gap-2">
                            {o.label}
                            {isTop && (
                              <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest bg-edge text-black px-1.5 py-0.5">
                                <Trophy size={9} weight="fill" /> Top pick
                              </span>
                            )}
                          </div>
                          {o.rationale && (
                            <div className="text-xs text-zinc-500 font-body line-clamp-1 mt-0.5">
                              {o.rationale}
                            </div>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-mono text-lg tabular-nums text-edge">
                            {formatPct(o.probability, 0)}
                          </div>
                          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                            AI
                          </div>
                        </div>
                        <div className="text-right shrink-0 w-16">
                          <div className="font-mono text-lg tabular-nums text-zinc-300">
                            {formatPct(o.marketPrice, 0)}
                          </div>
                          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                            MKT
                          </div>
                        </div>
                        <div className={`text-right shrink-0 w-20 font-mono tabular-nums text-sm ${edgeColor} inline-flex items-center justify-end gap-0.5`}>
                          {edge >= 0 ? <CaretUp size={10} weight="fill" /> : <CaretDown size={10} weight="fill" />}
                          {formatSignedPct(edge, 1)}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {!ranking && !loadingRank && (
              <div className="border border-white/10 p-6">
                <div className="text-[10px] uppercase tracking-[0.25em] font-mono text-zinc-500 mb-3">
                  Outcomes (raw market prices)
                </div>
                <div className="space-y-2">
                  {(event.markets || [])
                    .sort((a, b) => (b.yesPrice || 0) - (a.yesPrice || 0))
                    .map((m, i) => (
                      <Link
                        key={m.id}
                        to={`/market/${m.id}`}
                        className="flex items-center gap-3 p-3 border border-white/10 hover:border-white/30 transition-colors"
                      >
                        <span className="font-mono text-zinc-500 w-6">{i + 1}</span>
                        {m.image && <img src={m.image} alt="" className="w-8 h-8 object-cover border border-white/10" />}
                        <span className="flex-1 font-display text-sm truncate">
                          {m.groupItemTitle || m.question}
                        </span>
                        <span className="font-mono tabular-nums text-zinc-200">{formatPct(m.yesPrice, 0)}</span>
                      </Link>
                    ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
